import { Step, StepLabel, Stepper, TextField } from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { Button } from 'components/facets'
import { ActionCard } from 'components/facets/ActionCard'
import { useUser } from 'components/providers/UserProvider'
import CardmarketLogo from 'img/cardmarket-logo.svg'
import { editUserAttribute } from 'lib/api'
import { logoutUser } from 'lib/auth'
import { useAppContext } from 'lib/hooks'
import { useOnboardingStore } from 'lib/hooks/useOnboardingStore'
import { FetchedStockInterface } from 'lib/hooks/useStockStore'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect, useHistory } from 'react-router-dom'
import {
  commentSuggestsTaxation,
  IdGameInventoryArticles,
  InventoryArticle,
  ONBOARDING_SOURCE_SYNC,
  ONBOARDING_STEPS,
} from 'shared'
import { useCardmarketConnect } from '../cardmarket'
import { ChooseNextStep } from './ChooseNextStep'
import { CommentsForm } from './CommentsForm'
import { DownloadFetchedStock } from './DownloadFetchedStock'
import ExplainPowertools, { ExplainPowertoolsRef } from './ExplainPowertools'
import { FetchInventory } from './FetchInventory'
import { ImportCsvStock } from './ImportCsvStock'
import { InventoryCommentsAndLocationsTable } from './InventoryCommentsAndLocationsTable'
import { InventoryPriceTable } from './InventoryPriceTable'
import './onboarding-presenter.css'
import { getCommentsMapForeachInventoryArticle } from './onboarding-utils'
import { PaymentMethod } from './PaymentMethod'
import { Welcome } from './Welcome'

/***
 * check if within the fetched or imported stock, for each identified ptProduct there is more than 1 comment,
 * or if the comments present include keywords that typically refer to taxation
 */
export const checkIfFetchedStockCommentContainsTaxationKeywords = (
  inventory: InventoryArticle[]
) => {
  const mapOfItemsComments = getCommentsMapForeachInventoryArticle(inventory)
  const containsDiffOrMWST = Object.values(mapOfItemsComments).some(
    (entry) => entry.comments.some(commentSuggestsTaxation) || entry.comments.length > 1
  )
  return containsDiffOrMWST
}

export const OnboardingPresenter = (): JSX.Element => {
  const history = useHistory()
  const { t } = useTranslation()
  // const { handle } = useErrorHandler()
  // const { enqueueSnackbar } = useSnackbar()

  const { isAuthenticated } = useAppContext()
  const { setAuthenticated } = useAppContext()

  const { user, setUser } = useUser()

  const [shopifyStoreName, setShopifyStoreName] = useState('')
  // const { setShowSpinnerLoading } = useTransactionsSpinnerStore()
  const [connectCardmarketAccount, Popup] = useCardmarketConnect()
  const [openAskForHelpDialog, setOpenAskForHelpDialog] = useState(false)
  const {
    onboardingStep,
    setOnboardingStep,
    downloadStockSource,
    setDownloadStockSource,
    setFinalInventoryToSave,
    setVisitEndOnboarding,
  } = useOnboardingStore()

  const [inventoryState, setInventoryState] = useState<FetchedStockInterface>()

  const [changeFlowWithFixCommentsStep, setChangeFlowWithFixCommentsStep] = useState(false)
  const [
    updatedStockAfterSettingsPricesAndComments,
    setUpdatedStockAfterSettingsPricesAndComments,
  ] = useState<InventoryArticle[]>([])
  const explainPowertoolsRef = useRef<ExplainPowertoolsRef | null>(null)

  /**
   * since the user can choose to fetch from cardmarket or via csv, as a consequence he could see different pages.
   * I therefore implement a queue of visited pages so that when the user goes back he does not see pages that do not conform to the path he took
   */
  const [visitedSteps, setVisitedSteps] = useState<number[]>([ONBOARDING_STEPS.WELCOME.idx])

  visitedSteps

  const handleNext = () => {
    if (onboardingStep === ONBOARDING_STEPS.STEP_SHOPIFY.idx) {
      setOnboardingStep(ONBOARDING_STEPS.IMPORT_CSV.idx)
    } else {
      setVisitedSteps((prev) => [...prev, onboardingStep + 1])
      setOnboardingStep(onboardingStep + 1)
    }
  }

  const customHandleNext = () => {
    explainPowertoolsRef.current?.goToNextStep()
  }

  const handleBack = () => {
    setVisitedSteps((prev) => {
      if (onboardingStep === ONBOARDING_STEPS.CONTACTS_FOR_HELP.idx) {
        if (prev.length > 1) {
          const newVisitedSteps = prev.slice(0, -2)
          setOnboardingStep(newVisitedSteps[newVisitedSteps.length - 1])
          return newVisitedSteps
        }
      }
      if (prev.length > 1) {
        const newVisitedSteps = prev.slice(0, -1)
        setOnboardingStep(newVisitedSteps[newVisitedSteps.length - 1])
        return newVisitedSteps
      }
      return prev
    })
  }

  /* I use these objects to have an array on which to iterate the stepper, the content of these labels is irrelevant. 
  I use two different objects based on the flow that has been created because if the user were to fix the comments 
  the flow becomes longer*/
  const flowWithoutFixCommentsStep = [
    'Welcome',
    'Connect Cardmarket',
    'Sepa mandate',
    'Explain Powertools',
    'Stock source',
    'Loading',
    'Set price',
  ]

  const onboardingStepsWithoutFixCommentsStep: { [key: number]: number } = {
    [ONBOARDING_STEPS.WELCOME.idx]: 0,
    [ONBOARDING_STEPS.CONNECT_CARDMARKET_ACCOUNT.idx]: 1,
    [ONBOARDING_STEPS.SEPA_MANDATE.idx]: 2,
    [ONBOARDING_STEPS.HOW_POWERTOOLS_WORKS.idx]: 3,
    [ONBOARDING_STEPS.EMPTY_STOCK.idx]: 4,
    [ONBOARDING_STEPS.LOADING.idx]: 5,
    [ONBOARDING_STEPS.SET_PRICES.idx]: 6,
  }

  const flowWithFixCommentsStep = [
    'Welcome',
    'Connect Cardmarket',
    'Sepa mandate',
    'Explain Powertools',
    'Stock source',
    'Loading',
    'Comments questionnaire',
    'Set price',
    'Choose next step',
    'Set comments or Csv or finish',
  ]

  const onboardingStepsWithFixCommentsStep: { [key: number]: number } = {
    [ONBOARDING_STEPS.WELCOME.idx]: 0,
    [ONBOARDING_STEPS.CONNECT_CARDMARKET_ACCOUNT.idx]: 1,
    [ONBOARDING_STEPS.SEPA_MANDATE.idx]: 2,
    [ONBOARDING_STEPS.HOW_POWERTOOLS_WORKS.idx]: 3,
    [ONBOARDING_STEPS.EMPTY_STOCK.idx]: 4,
    [ONBOARDING_STEPS.LOADING.idx]: 5,
    [ONBOARDING_STEPS.COMMENTS_FORM.idx]: 6,
    [ONBOARDING_STEPS.SET_PRICES.idx]: 7,
    [ONBOARDING_STEPS.CHOOSE_NEXT_STEP.idx]: 8,
    [ONBOARDING_STEPS.IMPORT_CSV.idx]: 9,
  }

  const getActiveStepperIndex = (step: number) => {
    const activeIndices = changeFlowWithFixCommentsStep
      ? Object.keys(onboardingStepsWithFixCommentsStep)
          .map(Number)
          .filter((key) => key <= step)
          .map((key) => onboardingStepsWithFixCommentsStep[key])
      : Object.keys(onboardingStepsWithoutFixCommentsStep)
          .map(Number)
          .filter((key) => key <= step)
          .map((key) => onboardingStepsWithoutFixCommentsStep[key])

    return activeIndices.length > 0 ? Math.max(...activeIndices) : -1
  }

  const activeStepperIndex = getActiveStepperIndex(onboardingStep)

  const handleLogout = (): void => {
    logoutUser()
    setAuthenticated(false)
    //@ts-ignore
    setUser(null)
    history.push('/signin')
  }

  /**
   * Change the hasOnboarded variable in database and start writing transactions
   */
  const goToEndOnboardingStep = async (updatedInventory: InventoryArticle[], source?: string) => {
    const newPtInventory = {
      ...user.ptInventory,
      lastManualSyncWithCardmarketAt: new Date(),
    }
    await editUserAttribute(newPtInventory, 'ptInventory')
    setVisitEndOnboarding(true)

    const onboardingProgress = {
      onboardingSourceSync: source ?? downloadStockSource,
      startedWritingPowertoolsTransactionsAt: new Date(),
      finishedWritingPowertoolsTransactionsAt: [],
    }
    await editUserAttribute(onboardingProgress, 'completeOnboarding')

    const inventoryDividedByIdGame: IdGameInventoryArticles = {}

    updatedInventory.forEach((item) => {
      const key = item.card.idGame ?? 0
      if (!inventoryDividedByIdGame[key]) {
        inventoryDividedByIdGame[key] = []
      }
      inventoryDividedByIdGame[key].push(item)
    })

    setFinalInventoryToSave(inventoryDividedByIdGame)
    setTimeout(() => {
      history.push('/end-onboarding')
    }, 1500)
  }

  /**
   * Opens the pop up to log in. If the cardmarket username is already filled in, then go to the next step
   */
  const logInToCardmarket = async () => {
    if (!user.cardmarketUsername || user.cardmarketUsername === '') {
      await connectCardmarketAccount()
    } else {
      setVisitedSteps((prev) => [...prev, ONBOARDING_STEPS.SEPA_MANDATE.idx])
      setOnboardingStep(ONBOARDING_STEPS.SEPA_MANDATE.idx)
    }
  }

  /**
   * user could connect his cardmarket account or skip this step
   */
  const stepConnectCardmarket = () => {
    return (
      <div className="d-flex justify-content-center w-100 onboarding-container">
        <div className="wizard-content mt-lg-5 container">
          <div className="row justify-content-between">
            <div className="col-lg-6">
              <h3 className="fw-normal">{t('onboarding.connectCmTitle')}</h3>
              <h4 className="mb-4 mb-lg-5 fw-light">{t('onboarding.connectCardmarket')}</h4>
            </div>
            <div className="col-lg-6">
              <div className="mb-4">
                <ActionCard
                  variant="withDescription"
                  title={t('onboarding.connectCmButton')}
                  description={t('onboarding.connectCmDescription')}
                  button={
                    <Button
                      variant="contained"
                      data-testid="connect-cm"
                      onClick={() => logInToCardmarket()}
                      style={{ width: '200px' }}
                    >
                      Connect
                    </Button>
                  }
                />
              </div>

              <ActionCard
                variant="withDescription"
                title={t('onboarding.iDontHaveCm')}
                description={t('onboarding.dontConnectCmDescription')}
                button={
                  <Button
                    variant="outlined"
                    data-testid="dont-connect-cm"
                    style={{ width: '200px' }}
                    onClick={handleNext}
                  >
                    Skip
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  /**
   *
   * User selects the source from which to import his stock. Or he can choose not to import any stock
   */
  const stepSelectSourceFromWhichImportStock = () => {
    return (
      <div className="d-flex justify-content-center w-100 onboarding-container">
        <div className="wizard-content mt-lg-5 container">
          <div className="row justify-content-between">
            <div className="col-lg-6 mb-lg-0 mb-4">
              <h3 className="fw-normal">{t('onboarding.emptyInventory')}</h3>
            </div>
            <div className="col-lg-6">
              <div className="mb-4">
                <ActionCard
                  variant="withTitleIcon"
                  className="import-stock"
                  title={`${t('onboarding.importFrom')} Cardmarket`}
                  iconImg={
                    <div>
                      <img
                        src={CardmarketLogo}
                        style={{
                          height: '45px',
                          filter:
                            'brightness(0) saturate(100%) invert(9%) sepia(67%) saturate(5154%) hue-rotate(231deg) brightness(110%) contrast(116%)',
                        }}
                      />
                    </div>
                  }
                  button={
                    <Button
                      variant="contained"
                      data-testid="cardmarket-stock"
                      onClick={() => {
                        setDownloadStockSource(ONBOARDING_SOURCE_SYNC.CARDMARKET)
                        setOnboardingStep(ONBOARDING_STEPS.LOADING.idx)
                      }}
                      disabled={!user.cardmarketUsername || user.cardmarketUsername === ''}
                      tooltip={
                        user.cardmarketUsername === ''
                          ? `${t('onboarding.needConnectedCardmarketAccount')}`
                          : ''
                      }
                    >
                      <span>Import</span>
                    </Button>
                  }
                />
              </div>

              {/* {user.featureFlags != null && user.featureFlags.cardtrader && (
                <div className="mb-4">
                  <ActionCard
                    variant="withTitleIcon"
                    className="import-stock"
                    title={`${t('onboarding.importFrom')} CardTrader`}
                    iconImg={
                      <div>
                        <img
                          src={CardtraderWhiteLogo}
                          style={{
                            height: '45px',
                            filter:
                              'brightness(0) saturate(100%) invert(9%) sepia(67%) saturate(5154%) hue-rotate(231deg) brightness(110%) contrast(116%)',
                          }}
                        />
                      </div>
                    }
                    button={
                      <Button
                        variant="contained"
                        data-testid="cardtrader-stock"
                        onClick={() => {
                          if (
                            user.assignedCardtraderUser?.cardtraderUserName !== undefined &&
                            user.assignedCardtraderUser.cardtraderUserName !== ''
                          ) {
                            setDownloadStockSource(ONBOARDING_SOURCE_SYNC.CARDTRADER)
                            setOnboardingStep(ONBOARDING_STEPS.LOADING.idx)
                          } else {
                            setDownloadStockSource(ONBOARDING_SOURCE_SYNC.CARDTRADER)
                            popupCardTraderWindow(
                              600,
                              700,
                              window.screen.width / 2,
                              window.screen.height / 2,
                              fetchUser,
                              false
                            )
                          }
                        }}
                      >
                        <span>Import</span>
                      </Button>
                    }
                  />
                </div>
              )} */}

              <div className="mb-4">
                <ActionCard
                  variant="withTitleIcon"
                  className="import-stock"
                  title={`${t('onboarding.importFrom')} CSV file`}
                  iconClass="fa-light fa-file-csv"
                  button={
                    <Button
                      variant="contained"
                      data-testid="csv-stock"
                      onClick={() => {
                        setDownloadStockSource(ONBOARDING_SOURCE_SYNC.CSV)
                        setOnboardingStep(ONBOARDING_STEPS.IMPORT_CSV.idx)
                      }}
                    >
                      <span>Import</span>
                    </Button>
                  }
                />
              </div>

              {user.featureFlags != null && user.featureFlags.shopify && (
                <div className="mb-4">
                  <ActionCard
                    variant="withTitleIcon"
                    className="import-stock"
                    title={`${t('onboarding.importFrom')} Shopify`}
                    iconClass="fa-brands fa-shopify"
                    button={
                      <Button
                        variant="contained"
                        onClick={() => {
                          setDownloadStockSource(ONBOARDING_SOURCE_SYNC.SHOPIFY)
                          setOnboardingStep(ONBOARDING_STEPS.STEP_SHOPIFY.idx)
                        }}
                      >
                        <span>Import</span>
                      </Button>
                    }
                  />
                </div>
              )}

              <div className="mb-4">
                <ActionCard
                  variant="withTitleIcon"
                  className="import-stock"
                  title={t('onboarding.dontImportAnyStock')}
                  iconClass="fa-light fa-empty-set"
                  button={
                    <Button
                      variant="contained"
                      data-testid="skip-import"
                      onClick={() => {
                        setDownloadStockSource(ONBOARDING_SOURCE_SYNC.NO_STOCK)
                        goToEndOnboardingStep([], ONBOARDING_SOURCE_SYNC.NO_STOCK)
                      }}
                      style={{ width: '78px' }}
                    >
                      <span>Skip</span>
                    </Button>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  /**
   * User chooses which price apply to cards after loading stock from csv or fetching from cardmarket
   */
  const stepSetPrices = () => {
    return (
      <div className="d-flex justify-content-center w-100 onboarding-container">
        <div className="wizard-content container">
          <h3 className="fw-normal">{t('onboarding.selectPrices')}</h3>
          <p className="fs-6 fw-light">{t('onboarding.explainCsvPrices')}</p>
          <p className="fs-6 fw-light">
            {t('onboarding.pricesPushedToStore')}
            {user.cardmarketUsername !== '' && 'Cardmarket'}
            {/* <span> </span>
            {user.assignedCardtraderUser?.cardtraderUserName !== '' && 'CardTrader'} */}
          </p>
          <InventoryPriceTable
            fetchedStock={inventoryState!.stock.concat(inventoryState!.sealedStock)}
            setUpdatedStockAfterSettingsPricesAndComments={
              setUpdatedStockAfterSettingsPricesAndComments
            }
            confirmLabel={
              changeFlowWithFixCommentsStep ? t('common.confirm') : t('onboarding.confirmAndSave')
            }
            onConfirm={(updatedInventory) => {
              if (changeFlowWithFixCommentsStep) {
                setVisitedSteps((prev) => [...prev, ONBOARDING_STEPS.CHOOSE_NEXT_STEP.idx])
                setOnboardingStep(ONBOARDING_STEPS.CHOOSE_NEXT_STEP.idx)
              } else {
                goToEndOnboardingStep(updatedInventory)
              }
            }}
          />
        </div>
      </div>
    )
  }

  // const stepEndOnboarding = () => {
  //   return (
  //     <div className="w-100 d-flex justify-content-center onboarding-container px-5">
  //       <div className="text-center px-5 py-5 container-small">
  //         <h1 className="congratulations-onboarding">{t('onboarding.congratulations')}!</h1>
  //         <h4 className="fw-light mb-5">{t('onboarding.endOnboarding1')}</h4>

  //         <div className="onboarding-notes-box mb-5">
  //           <h4 className="fw-light my-2">{t('onboarding.endOnboarding2')}</h4>
  //           <h5 className="fw-light my-4">{t('onboarding.endOnboarding3')}</h5>
  //           <h5 className="fw-light my-4">{t('onboarding.endOnboarding4')}</h5>
  //           <h5 className="fw-light my-2">{t('onboarding.endOnboarding5')}</h5>
  //         </div>

  //         <Button
  //           variant="contained"
  //           scaleUp={true}
  //           onClick={() => setUserOnboarded()}
  //           data-testid="end-onboarding"
  //         >
  //           {t('onboarding.startUsingPowertools')}
  //         </Button>
  //       </div>
  //     </div>
  //   )
  // }

  const stepSyncFromShopify = () => {
    return (
      <div className="d-flex justify-content-center w-100 onboarding-container">
        <div className="wizard-content mt-lg-5 container">
          <h3 className="fw-normal">{t('onboarding.howToSyncShopify')}</h3>
          <p className="fs-6 fw-light">{t('onboarding.syncShopifyStart')}</p>
          <ul>
            <li className="fs-6 fw-light">
              <div className="d-flex flex-row align-items-center">
                Insert your shopify store name:
                <TextField
                  className="ms-2"
                  helperText="eg: my-shopify-store"
                  value={shopifyStoreName}
                  onChange={(e) => setShopifyStoreName(e.target.value)}
                />{' '}
              </div>
            </li>
            <li className="mb-3 fs-6 fw-light">
              {t('onboarding.syncShopifyOne')}:{' '}
              <a
                className="ms-1"
                href={`https://admin.shopify.com/store/${shopifyStoreName}/products?selectedView=all`}
              >
                SHOPIFY LINK
              </a>
            </li>
            <li className="mb-3 fs-6 fw-light">{t('onboarding.syncShopifyTwo')}</li>
            <li className="mb-3 fs-6 fw-light"> {t('onboarding.syncShopifyThree')}</li>
            <li className="mb-3 fs-6 fw-light">{t('onboarding.syncShopifyFour')}</li>
          </ul>
        </div>
      </div>
    )
  }

  /**
   * in case the user has a particular stock situation, he will be redirected to this page to invite him to contact us
   */
  const stepContactsForHelp = () => {
    return (
      <div className="d-flex w-100 onboarding-container">
        <div className="wizard-content mt-lg-5 container d-flex flex-column align-items-center">
          <h3 className="pt-5 fw-normal">{t('onboarding.contanctUs')}</h3>

          <p className="fs-6 fw-light">{t('onboarding.contactUsDescription')}</p>
          <div className="w-75 d-flex flex-row justify-content-between container-small">
            <div className="ask-for-help-box">
              <div
                className="d-flex flex-column justify-content-center align-items-center h-100"
                onClick={() => {
                  window.open('https://discord.gg/y7Rz34hgqJ', '_blank')
                }}
              >
                <i className="fa-brands fa-discord mb-1"></i>
                <p>{t('onboarding.contactDiscord')}</p>
              </div>
            </div>
            <div className="ask-for-help-box">
              <div
                className="d-flex flex-column justify-content-center align-items-center h-100"
                onClick={() => (window.location.href = 'mailto:team@tcgpowertools.com')}
              >
                <i className="fa-regular fa-envelope mb-1"></i>
                <p>{t('onboarding.contactEmail')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (
      user.cardmarketUsername === ''
      // &&
      // (user.assignedCardtraderUser === undefined ||
      //   (user.assignedCardtraderUser?.cardtraderUserName === '' && downloadStockSource === ''))
    ) {
      setOnboardingStep(0)
      setVisitedSteps([0])
    }
    // CARDMARKET
    // This is to ensure that onboarding does not restart from 0 when the connection to CM is completed
    else if (
      user.cardmarketUsername !== '' &&
      onboardingStep === ONBOARDING_STEPS.CONNECT_CARDMARKET_ACCOUNT.idx
    ) {
      setVisitedSteps((prev) => [...prev, ONBOARDING_STEPS.SEPA_MANDATE.idx])
      setOnboardingStep(ONBOARDING_STEPS.SEPA_MANDATE.idx)
    }

    // CARDTRADER
    // if (
    //   user.assignedCardtraderUser &&
    //   user.assignedCardtraderUser?.cardtraderUserName !== '' &&
    //   downloadStockSource === ONBOARDING_SOURCE_SYNC.CARDTRADER
    // ) {
    //   setVisitedSteps((prev) => [...prev, ONBOARDING_STEPS.LOADING.idx])
    //   setOnboardingStep(ONBOARDING_STEPS.LOADING.idx)
    // }
    // }, [user.cardmarketUsername, user.assignedCardtraderUser?.cardtraderUserName])
  }, [user.cardmarketUsername])

  useEffect(() => {
    if (onboardingStep === ONBOARDING_STEPS.COMMENTS_FORM.idx)
      setChangeFlowWithFixCommentsStep(true)
  }, [onboardingStep])

  if (!isAuthenticated) return <Redirect to="/signin" />
  else
    return (
      <div className="d-flex flex-column justify-content-between h-100">
        <div className="stepper-plus-content">
          <div
            className={`d-flex justify-content-center w-100 onboarding-stepper ${
              onboardingStep === ONBOARDING_STEPS.WELCOME.idx ||
              onboardingStep === ONBOARDING_STEPS.CONTACTS_FOR_HELP.idx
                ? 'd-none'
                : ''
            }
              ${changeFlowWithFixCommentsStep ? ' expanded-width ' : ' standard-width '}
              
              `}
          >
            {changeFlowWithFixCommentsStep ? (
              <Stepper activeStep={activeStepperIndex} alternativeLabel>
                {flowWithFixCommentsStep.map((label) => {
                  const stepProps: { completed?: boolean } = {}
                  return (
                    <Step key={label} {...stepProps}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  )
                })}
              </Stepper>
            ) : (
              <Stepper activeStep={activeStepperIndex} alternativeLabel>
                {flowWithoutFixCommentsStep.map((label) => {
                  const stepProps: { completed?: boolean } = {}
                  return (
                    <Step key={label} {...stepProps}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  )
                })}
              </Stepper>
            )}
          </div>
        </div>

        <div className="onboarding px-3 px-md-0" style={{ zIndex: '100', flexGrow: '1' }}>
          {onboardingStep === ONBOARDING_STEPS.WELCOME.idx && <Welcome handleNext={handleNext} />}
          {onboardingStep === ONBOARDING_STEPS.CONNECT_CARDMARKET_ACCOUNT.idx &&
            stepConnectCardmarket()}
          {onboardingStep === ONBOARDING_STEPS.SEPA_MANDATE.idx && (
            <PaymentMethod handleNext={handleNext} />
          )}
          {onboardingStep === ONBOARDING_STEPS.HOW_POWERTOOLS_WORKS.idx && (
            <ExplainPowertools ref={explainPowertoolsRef} handleNext={handleNext} />
          )}
          {onboardingStep === ONBOARDING_STEPS.EMPTY_STOCK.idx &&
            stepSelectSourceFromWhichImportStock()}
          {onboardingStep === ONBOARDING_STEPS.LOADING.idx && (
            <FetchInventory
              setOnboardingStep={setOnboardingStep}
              setVisitedSteps={setVisitedSteps}
              inventoryState={inventoryState!}
              setInventoryState={setInventoryState}
              downloadStockSource={downloadStockSource}
            />
          )}
          {onboardingStep === ONBOARDING_STEPS.COMMENTS_FORM.idx && (
            <CommentsForm setVisitedSteps={setVisitedSteps} />
          )}
          {onboardingStep === ONBOARDING_STEPS.SET_PRICES.idx && stepSetPrices()}
          {onboardingStep === ONBOARDING_STEPS.CHOOSE_NEXT_STEP.idx && (
            <ChooseNextStep
              setVisitedSteps={setVisitedSteps}
              inventoryState={inventoryState!}
              setUserOnboarded={goToEndOnboardingStep}
            />
          )}
          {onboardingStep === ONBOARDING_STEPS.SET_COMMENTS_LOCATIONS.idx && (
            <InventoryCommentsAndLocationsTable
              fetchedStock={updatedStockAfterSettingsPricesAndComments}
              onConfirm={goToEndOnboardingStep}
            />
          )}
          {onboardingStep === ONBOARDING_STEPS.DOWNLOAD_FETCHED_STOCK.idx && (
            <DownloadFetchedStock
              setVisitedSteps={setVisitedSteps}
              fetchedStock={updatedStockAfterSettingsPricesAndComments}
              handleNext={handleNext}
            />
          )}

          {onboardingStep === ONBOARDING_STEPS.IMPORT_CSV.idx && (
            <ImportCsvStock
              changeFlowWithFixCommentsStep={changeFlowWithFixCommentsStep}
              setVisitedSteps={setVisitedSteps}
              setStep={setOnboardingStep}
              setInventoryState={setInventoryState}
              setUserOnboarded={goToEndOnboardingStep}
            />
          )}
          {onboardingStep === ONBOARDING_STEPS.CONTACTS_FOR_HELP.idx && stepContactsForHelp()}
          {onboardingStep === ONBOARDING_STEPS.STEP_SHOPIFY.idx && stepSyncFromShopify()}
        </div>

        <div className={`logout-button p-3 p-sm-2 p-lg-5 w-100`}>
          <div
            className={`w-100 d-flex flex-row justify-content-between gap-3 ${
              onboardingStep === ONBOARDING_STEPS.WELCOME.idx ? 'd-none' : 'pb-3'
            }`}
          >
            <Button variant="outlined" onClick={handleBack} style={{ width: '200px' }}>
              {t('common.back')}
            </Button>

            <Button
              variant="contained"
              onClick={
                onboardingStep === ONBOARDING_STEPS.HOW_POWERTOOLS_WORKS.idx
                  ? customHandleNext
                  : handleNext
              }
              data-testid="next"
              style={{
                visibility:
                  onboardingStep === ONBOARDING_STEPS.HOW_POWERTOOLS_WORKS.idx ||
                  onboardingStep === ONBOARDING_STEPS.STEP_SHOPIFY.idx ||
                  onboardingStep === ONBOARDING_STEPS.DOWNLOAD_FETCHED_STOCK.idx
                    ? 'visible'
                    : 'hidden',
                width: '200px',
              }}
            >
              {t('common.next')}
            </Button>
          </div>
          <div className="w-100 d-flex flex-row justify-content-between gap-3">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleLogout}
              style={{ width: '200px' }}
            >
              <i className="fa-solid fa-arrow-left-to-bracket pe-1"></i>
              {t('navigation.logout')}
            </Button>
            <Button
              onClick={() => setOpenAskForHelpDialog(true)}
              color="primary"
              variant="outlined"
              style={{
                width: '200px',
                visibility:
                  onboardingStep === ONBOARDING_STEPS.WELCOME.idx ||
                  onboardingStep === ONBOARDING_STEPS.CONNECT_CARDMARKET_ACCOUNT.idx ||
                  onboardingStep === ONBOARDING_STEPS.SEPA_MANDATE.idx ||
                  onboardingStep === ONBOARDING_STEPS.LOADING.idx ||
                  onboardingStep === ONBOARDING_STEPS.CONTACTS_FOR_HELP.idx
                    ? 'hidden'
                    : 'visible',
              }}
            >
              <div>
                <i className="fa-brands fa-discord me-1"></i>
                {t('onboarding.askForHelpBtn')}
              </div>
            </Button>
          </div>
        </div>

        <Popup />
        <ClosableDialog open={openAskForHelpDialog} onClose={() => setOpenAskForHelpDialog(false)}>
          <p className="my-4 fs-6 text-center">{t('onboarding.contactUsChoose')}</p>
          <div className="w-100 d-flex flex-row justify-content-between">
            <div className="ask-for-help-box">
              <div
                className="d-flex flex-column justify-content-center align-items-center h-100"
                onClick={() => {
                  window.open('https://discord.gg/y7Rz34hgqJ', '_blank')
                }}
              >
                <i className="fa-brands fa-discord mb-1"></i>
                <p>{t('onboarding.contactDiscord')}</p>
              </div>
            </div>
            <div className="ask-for-help-box">
              <div
                className="d-flex flex-column justify-content-center align-items-center h-100"
                onClick={() => (window.location.href = 'mailto:team@tcgpowertools.com')}
              >
                <i className="fa-regular fa-envelope mb-1"></i>
                <p>{t('onboarding.contactEmail')}</p>
              </div>
            </div>
          </div>
        </ClosableDialog>
      </div>
    )
}
